import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { documentsApi, employeesApi } from '../api';
import {
  Badge,
  EmptyState,
  HeroPanel,
  Panel,
  PortalShell,
  StatCard,
  getSessionUser
} from '../components/PortalUI';
import config from '../config';
import { dateOnly, money, qrImage } from '../utils';

const navItems = ['Overview', 'Profile', 'Payroll', 'Documents', 'Verification'];

const payroll = (salary = 0) => {
  const basic = Number(salary || 0);
  const hra = Math.round(basic * 0.2);
  const da = Math.round(basic * 0.1);
  const pf = Math.round(basic * 0.12);
  const tax = Math.round(basic * 0.05);
  return { basic, hra, da, pf, tax, net: basic + hra + da - pf - tax };
};

function EmployeeDashboard() {
  const [active, setActive] = useState('Overview');
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = getSessionUser();
  const pay = useMemo(() => payroll(profile?.salary), [profile]);
  const verifyUrl = `${config.APP_URL}/verify/employee/${userId}`;

  const load = useCallback(async () => {
    setLoading(true);
    const [profileData, docData] = await Promise.all([
      employeesApi.get(userId).catch(() => null),
      documentsApi.list(userId).catch(() => [])
    ]);
    setProfile(profileData);
    setDocuments(Array.isArray(docData) ? docData : []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const payrollRows = [
    ['Basic Salary', pay.basic, 'slate'],
    ['HRA 20%', pay.hra, 'emerald'],
    ['DA 10%', pay.da, 'blue'],
    ['PF Deduction 12%', -pay.pf, 'rose'],
    ['Tax Deduction 5%', -pay.tax, 'rose'],
    ['Net Salary', pay.net, 'emerald']
  ];

  return (
    <PortalShell
      role="employee"
      title={active}
      subtitle="A refined workspace for employee profile, payroll, documents and verification."
      navItems={navItems}
      active={active}
      onNavChange={setActive}
      actions={<button onClick={load} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Refresh</button>}
    >
      <div className="space-y-6">
        <HeroPanel
          role="employee"
          eyebrow="Employee record"
          title={`Welcome back, ${profile?.name || userId}`}
          right={<img src={qrImage(verifyUrl)} alt="Employee verification QR" className="h-28 w-28 rounded-lg bg-white p-2 shadow-lg" />}
        >
          {profile?.designation || 'Employee'} in {profile?.department || 'your department'}, connected to secure payroll and document verification.
        </HeroPanel>

        {active === 'Overview' && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <StatCard label="Department" value={profile?.department || '-'} tone="text-emerald-600" />
              <StatCard label="Designation" value={profile?.designation || '-'} tone="text-blue-600" />
              <StatCard label="Net Salary" value={money(pay.net)} tone="text-violet-600" />
              <StatCard label="Documents" value={documents.length} tone="text-amber-600" />
            </div>
            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
              <Panel title="Payroll Snapshot" subtitle="Estimated monthly salary calculation.">
                <div className="grid gap-3 sm:grid-cols-2">
                  {payrollRows.map(([label, value, tone]) => (
                    <div key={label} className="rounded-lg bg-slate-50 p-4">
                      <p className="text-xs font-black uppercase text-slate-400">{label}</p>
                      <p className={`mt-1 text-xl font-black ${Number(value) < 0 ? 'text-rose-600' : tone === 'emerald' ? 'text-emerald-600' : tone === 'blue' ? 'text-blue-600' : 'text-slate-800'}`}>{money(value)}</p>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Record Health" subtitle="Profile verification summary.">
                <div className="space-y-3">
                  <Badge tone="emerald">Profile active</Badge>
                  <p className="text-sm leading-6 text-slate-500">Your employee record is available for secure QR verification. Payroll numbers are calculated locally from your saved salary field.</p>
                  <button onClick={() => setActive('Verification')} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700">Open verification</button>
                </div>
              </Panel>
            </div>
          </>
        )}

        {active === 'Profile' && (
          <Panel title="My Profile" subtitle="Employee identity and contact information.">
            {profile ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries({
                  'Full Name': profile.name,
                  'Employee ID': profile.employeeId,
                  Department: profile.department,
                  Designation: profile.designation,
                  Email: profile.email,
                  'Joined On': dateOnly(profile.createdAt)
                }).map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-slate-50 p-4">
                    <p className="text-xs font-black uppercase text-slate-400">{label}</p>
                    <p className="mt-1 break-words font-bold text-slate-800">{value || '-'}</p>
                  </div>
                ))}
              </div>
            ) : loading ? <EmptyState title="Loading profile" text="Fetching your employee record." /> : <EmptyState title="Profile not found" text="Please contact your administrator." />}
          </Panel>
        )}

        {active === 'Payroll' && (
          <Panel title="Payroll Information" subtitle="Printable salary slip style summary." actions={<button onClick={() => window.print()} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700">Print / Save PDF</button>}>
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-black uppercase text-slate-400">Salary slip for</p>
              <h3 className="mt-1 text-2xl font-black text-slate-900">{profile?.name || userId}</h3>
              <p className="text-sm text-slate-500">{profile?.employeeId || userId} - {profile?.designation || '-'}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {payrollRows.map(([label, value], index) => (
                <div key={label} className={`rounded-lg border p-4 ${index === payrollRows.length - 1 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-slate-50'}`}>
                  <p className="text-xs font-black uppercase text-slate-400">{label}</p>
                  <p className={`mt-1 text-2xl font-black ${Number(value) < 0 ? 'text-rose-600' : index === payrollRows.length - 1 ? 'text-emerald-700' : 'text-slate-800'}`}>{money(value)}</p>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {active === 'Documents' && (
          <Panel title="Documents" subtitle="Files attached to your employee record.">
            {documents.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {documents.map((doc) => (
                  <a key={doc.documentId} href={doc.downloadUrl || '#'} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 p-4 hover:bg-slate-50">
                    <p className="font-black text-slate-800">{doc.fileName}</p>
                    <p className="mt-1 text-xs font-bold text-slate-400">{doc.status} - {dateOnly(doc.createdAt)}</p>
                  </a>
                ))}
              </div>
            ) : <EmptyState title="No documents uploaded" text="HR documents and verification files will appear here." />}
          </Panel>
        )}

        {active === 'Verification' && (
          <Panel title="Verification QR" subtitle="Use this QR code to verify your active employee record.">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <img src={qrImage(verifyUrl)} alt="Employee verification QR" className="h-44 w-44 rounded-lg border border-slate-200 bg-white p-3 shadow-sm" />
              <div>
                <p className="text-sm text-slate-500">Public verification URL</p>
                <p className="mt-2 break-all rounded-lg bg-slate-50 p-3 text-sm font-bold text-slate-700">{verifyUrl}</p>
              </div>
            </div>
          </Panel>
        )}
      </div>
    </PortalShell>
  );
}

export default EmployeeDashboard;
